import { api } from './api'
import { 
  PatientClinicalHistory, 
  ConditionFormData, 
  FamilyHistoryFormData,
  PreferencesFormData,
  ConsentFormData,
  PatientCondition,
  FamilyHistory,
  PatientConsent
} from '../types/patientHistory'

export const patientHistoryService = {
  /**
   * Get comprehensive clinical history for a patient
   */
  getClinicalHistory: async (patientId: string): Promise<PatientClinicalHistory | null> => {
    try {
      const response = await api.get<PatientClinicalHistory>(`/patients/${patientId}/clinical-history`)
      return response.data
    } catch (error: any) {
      // Return null if not found (history may not exist yet)
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Create or update clinical history for a patient
   */
  saveClinicalHistory: async (patientId: string, history: Partial<PatientClinicalHistory>): Promise<PatientClinicalHistory> => {
    const response = await api.put<PatientClinicalHistory>(`/patients/${patientId}/clinical-history`, history)
    return response.data
  },

  /**
   * Add a new condition to patient history
   */
  addCondition: async (patientId: string, condition: ConditionFormData): Promise<PatientCondition> => {
    const response = await api.post<PatientCondition>(`/patients/${patientId}/clinical-history/conditions`, condition)
    return response.data
  },

  /**
   * Update an existing condition
   */
  updateCondition: async (patientId: string, conditionId: string, condition: Partial<ConditionFormData>): Promise<PatientCondition> => {
    const response = await api.put<PatientCondition>(`/patients/${patientId}/clinical-history/conditions/${conditionId}`, condition)
    return response.data
  },

  /**
   * Remove a condition (mark as resolved or delete)
   */
  removeCondition: async (patientId: string, conditionId: string): Promise<void> => {
    await api.delete(`/patients/${patientId}/clinical-history/conditions/${conditionId}`)
  },

  /**
   * Add family history entry
   */
  addFamilyHistory: async (patientId: string, familyHistory: FamilyHistoryFormData): Promise<FamilyHistory> => {
    const response = await api.post<FamilyHistory>(`/patients/${patientId}/clinical-history/family-history`, familyHistory)
    return response.data
  },

  /**
   * Update family history entry
   */
  updateFamilyHistory: async (patientId: string, entryId: string, familyHistory: Partial<FamilyHistoryFormData>): Promise<FamilyHistory> => {
    const response = await api.put<FamilyHistory>(`/patients/${patientId}/clinical-history/family-history/${entryId}`, familyHistory)
    return response.data
  },

  /**
   * Remove family history entry
   */
  removeFamilyHistory: async (patientId: string, entryId: string): Promise<void> => {
    await api.delete(`/patients/${patientId}/clinical-history/family-history/${entryId}`)
  },

  /**
   * Update patient preferences
   */
  updatePreferences: async (patientId: string, preferences: Partial<PreferencesFormData>): Promise<import('../types/patientHistory').PatientPreferences> => {
    const response = await api.put<PatientClinicalHistory['preferences']>(`/patients/${patientId}/clinical-history/preferences`, preferences)
    return response.data
  },

  /**
   * Add a new consent record
   */
  addConsent: async (patientId: string, consent: ConsentFormData): Promise<PatientConsent> => {
    const response = await api.post<PatientConsent>(`/patients/${patientId}/clinical-history/consents`, consent)
    return response.data
  },

  /**
   * Update an existing consent record
   */
  updateConsent: async (patientId: string, consentId: string, consent: Partial<ConsentFormData>): Promise<PatientConsent> => {
    const response = await api.put<PatientConsent>(`/patients/${patientId}/clinical-history/consents/${consentId}`, consent)
    return response.data
  },

  /**
   * Withdraw consent
   */
  withdrawConsent: async (patientId: string, consentId: string, reason?: string): Promise<PatientConsent> => {
    const response = await api.post<PatientConsent>(`/patients/${patientId}/clinical-history/consents/${consentId}/withdraw`, { reason })
    return response.data
  },

  /**
   * Remove consent record
   */
  removeConsent: async (patientId: string, consentId: string): Promise<void> => {
    await api.delete(`/patients/${patientId}/clinical-history/consents/${consentId}`)
  },
}
